import React, { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import {
  DEFAULT_STOP_CATEGORIES,
  StopCategory,
  validateWordWithApi,
  WordValidationResult,
  calculateStopScores
} from '../../services/stopGameValidator';
import { getRandomUnusedLetter } from '../../services/stopGameRoomService';
import { playBuzzerSound, playSuccessSound, playTickSound } from '../../utils/stopGameAudio';
import StopLetterWheel from './StopLetterWheel';
import StopCategoryInput from './StopCategoryInput';
import StopWordReviewModal from './StopWordReviewModal';
import { Trophy, Timer, Play, RotateCcw, AlertTriangle, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserScores } from '../../services/leaderboardService';

interface StopGameSoloProps {
  onBackToMenu: () => void;
}

type GamePhase = 'setup' | 'playing' | 'validating' | 'round_review' | 'game_over';

export const SOLO_STORAGE_KEY = 'stop_game_solo_state';

export interface SavedSoloState {
  phase: GamePhase;
  totalRounds: number;
  timeLimit: number;
  selectedCategories: StopCategory[];
  currentRound: number;
  totalScore: number;
  currentLetter: string;
  usedLetters: string[];
  answers: Record<string, string>;
  timeLeft: number;
  roundEndTime?: number | null;
  validations: Record<string, WordValidationResult>;
  categoryScores: Record<string, number>;
  roundScore: number;
  roundHistory: Array<{ round: number; letter: string; score: number }>;
}

export const loadSavedSoloState = (): SavedSoloState | null => {
  try {
    const raw = localStorage.getItem(SOLO_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.phase && typeof parsed.currentRound === 'number') {
      return parsed;
    }
  } catch (e) {
    console.warn('Error reading saved solo state:', e);
  }
  return null;
};

export const StopGameSolo: React.FC<StopGameSoloProps> = ({ onBackToMenu }) => {
  const { currentUser } = useAuth();
  const initialSaved = useRef(loadSavedSoloState()).current;

  // Settings
  const [totalRounds, setTotalRounds] = useState(initialSaved?.totalRounds ?? 3);
  const [timeLimit, setTimeLimit] = useState(initialSaved?.timeLimit ?? 60); // seconds, 0 = unlimited
  const [selectedCategories, setSelectedCategories] = useState<StopCategory[]>(
    initialSaved?.selectedCategories ?? DEFAULT_STOP_CATEGORIES.slice(0, 6)
  );

  // Game state
  const [phase, setPhase] = useState<GamePhase>(initialSaved?.phase ?? 'setup');
  const [currentRound, setCurrentRound] = useState(initialSaved?.currentRound ?? 1);
  const [totalScore, setTotalScore] = useState(initialSaved?.totalScore ?? 0);
  const [currentLetter, setCurrentLetter] = useState(initialSaved?.currentLetter ?? 'A');
  const [usedLetters, setUsedLetters] = useState<string[]>(initialSaved?.usedLetters ?? []);
  const [answers, setAnswers] = useState<Record<string, string>>(initialSaved?.answers ?? {});

  const roundEndTimeRef = useRef<number | null>(initialSaved?.roundEndTime ?? null);

  const computeInitialTimeLeft = () => {
    if (!initialSaved || initialSaved.phase !== 'playing') {
      return initialSaved?.timeLeft ?? 60;
    }
    if (initialSaved.timeLimit === 0) return 0;
    if (initialSaved.roundEndTime) {
      const now = Date.now();
      return Math.max(0, Math.ceil((initialSaved.roundEndTime - now) / 1000));
    }
    return initialSaved.timeLeft ?? 60;
  };

  const [timeLeft, setTimeLeft] = useState<number>(computeInitialTimeLeft);
  const [isSpinning, setIsSpinning] = useState(false);

  // Validation results
  const [validations, setValidations] = useState<Record<string, WordValidationResult>>(initialSaved?.validations ?? {});
  const [categoryScores, setCategoryScores] = useState<Record<string, number>>(initialSaved?.categoryScores ?? {});
  const [roundScore, setRoundScore] = useState(initialSaved?.roundScore ?? 0);
  const [roundHistory, setRoundHistory] = useState<Array<{ round: number; letter: string; score: number }>>(
    initialSaved?.roundHistory ?? []
  );

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const answersRef = useRef<Record<string, string>>(initialSaved?.answers ?? {});
  const isStoppingRef = useRef(false);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // Save game state changes to localStorage so page refresh preserves progress
  useEffect(() => {
    if (phase === 'setup') {
      localStorage.removeItem(SOLO_STORAGE_KEY);
      return;
    }

    const stateToSave: SavedSoloState = {
      phase,
      totalRounds,
      timeLimit,
      selectedCategories,
      currentRound,
      totalScore,
      currentLetter,
      usedLetters,
      answers,
      timeLeft,
      roundEndTime: roundEndTimeRef.current,
      validations,
      categoryScores,
      roundScore,
      roundHistory
    };

    try {
      localStorage.setItem(SOLO_STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (e) {
      console.warn('Failed to save solo game state:', e);
    }
  }, [
    phase,
    totalRounds,
    timeLimit,
    selectedCategories,
    currentRound,
    totalScore,
    currentLetter,
    usedLetters,
    answers,
    timeLeft,
    validations,
    categoryScores,
    roundScore,
    roundHistory
  ]);

  // On mount: if restored in 'playing' and time has already expired while away, trigger stop
  useEffect(() => {
    if (initialSaved?.phase === 'playing' && initialSaved.timeLimit > 0) {
      const now = Date.now();
      if (initialSaved.roundEndTime && initialSaved.roundEndTime <= now) {
        handleStop();
      }
    } else if (initialSaved?.phase === 'validating') {
      handleStop();
    }
  }, []);

  // Timer countdown during playing phase (only active after letter spin finishes)
  useEffect(() => {
    if (phase !== 'playing' || timeLimit === 0 || isSpinning) return;

    timerRef.current = setInterval(() => {
      if (roundEndTimeRef.current && timeLimit > 0) {
        const remaining = Math.max(0, Math.ceil((roundEndTimeRef.current - Date.now()) / 1000));
        setTimeLeft(remaining);
        if (remaining <= 5 && remaining > 0) {
          playTickSound();
        }
        if (remaining <= 0) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          handleStop();
        }
      } else {
        setTimeLeft(prev => {
          if (prev <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            setTimeout(() => {
              handleStop();
            }, 0);
            return 0;
          }
          if (prev <= 5) {
            playTickSound();
          }
          return prev - 1;
        });
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [phase, timeLimit, isSpinning]);

  const startRound = (roundNum: number, existingUsedLetters: string[]) => {
    const nextLetter = getRandomUnusedLetter(existingUsedLetters);
    const updatedUsed = [...existingUsedLetters, nextLetter];

    const spinDuration = 3000;
    const endTime = timeLimit > 0 ? Date.now() + spinDuration + timeLimit * 1000 : null;
    roundEndTimeRef.current = endTime;

    setCurrentRound(roundNum);
    setCurrentLetter(nextLetter);
    setUsedLetters(updatedUsed);
    setAnswers({});
    answersRef.current = {};
    isStoppingRef.current = false;
    setTimeLeft(timeLimit);
    setIsSpinning(true);
    setPhase('playing');
  };

  const handleStartGame = () => {
    setTotalScore(0);
    setRoundHistory([]);
    setUsedLetters([]);
    startRound(1, []);
  };

  const handleAnswerChange = (categoryId: string, val: string) => {
    setAnswers(prev => ({ ...prev, [categoryId]: val }));
  };

  const handleBackToMenu = () => {
    localStorage.removeItem(SOLO_STORAGE_KEY);
    onBackToMenu();
  };

  const handleChangeSettings = () => {
    localStorage.removeItem(SOLO_STORAGE_KEY);
    setPhase('setup');
  };

  const handleStop = async () => {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    playBuzzerSound();
    setPhase('validating');

    try {
      const currentAnswers = answersRef.current;
      // Run API validation for each category
      const results: Record<string, WordValidationResult> = {};

      for (const cat of selectedCategories) {
        const userWord = currentAnswers[cat.id] || '';
        const validation = await validateWordWithApi(userWord, currentLetter, cat.id);
        results[cat.id] = validation;
      }

      // Accurate score calculation including -8 pts typo penalty
      const calculated = calculateStopScores({ solo: currentAnswers }, { solo: results });
      const scoreEarned = calculated.solo?.roundScore || 0;
      const catScores = calculated.solo?.categoryScores || {};

      setValidations(results);
      setCategoryScores(catScores);
      setRoundScore(scoreEarned);
      setTotalScore(prev => prev + scoreEarned);
      setRoundHistory(prev => [...prev, { round: currentRound, letter: currentLetter, score: scoreEarned }]);

      if (scoreEarned > 0) {
        playSuccessSound();
      }
    } catch (error) {
      console.error('Error during solo Stop validation:', error);
    } finally {
      setPhase('round_review');
    }
  };

  const handleNextRoundOrFinish = async () => {
    if (currentRound >= totalRounds) {
      // Game Over
      setPhase('game_over');
      try {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      } catch {
        // ignore
      }

      // If user is logged in, optionally update their leaderboard score batch at game end
      if (currentUser && totalScore > 0) {
        try {
          await updateUserScores(currentUser.uid, totalScore);
        } catch (e) {
          console.warn('Failed to sync STOP score to leaderboard:', e);
        }
      }
    } else {
      startRound(currentRound + 1, usedLetters);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Top Header & Navigation */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleBackToMenu}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors text-sm font-semibold"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Modes
        </button>

        {phase !== 'setup' && (
          <div className="flex items-center gap-4">
            <span className="text-sm font-bold text-gray-500">
              Round {currentRound} of {totalRounds}
            </span>
            <div className="flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 rounded-full font-black text-sm border border-purple-200">
              <Trophy className="w-4 h-4 text-amber-500" />
              <span>{totalScore} pts</span>
            </div>
          </div>
        )}
      </div>

      {/* SETUP PHASE */}
      {phase === 'setup' && (
        <div className="bg-white rounded-3xl p-6 md:p-10 shadow-xl border border-gray-100 text-center animate-in fade-in">
          <div className="w-16 h-16 bg-gradient-to-tr from-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg shadow-purple-500/30">
            <Play className="w-8 h-8 ml-1" />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900">Solo Practice Mode</h2>
          <p className="text-gray-600 mt-2 max-w-lg mx-auto">
            Test your English vocabulary speed against the clock! For each category, type a valid English word starting with the round&apos;s letter.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 max-w-xl mx-auto text-left">
            {/* Round Count */}
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-200">
              <label className="block text-sm font-bold text-gray-700 mb-2">Number of Rounds</label>
              <div className="grid grid-cols-3 gap-2">
                {[1, 3, 5].map(num => (
                  <button
                    key={num}
                    onClick={() => setTotalRounds(num)}
                    className={`py-2 rounded-xl font-bold text-sm transition-all ${
                      totalRounds === num
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {num} {num === 1 ? 'Round' : 'Rounds'}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Limit */}
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-200">
              <label className="block text-sm font-bold text-gray-700 mb-2">Time per Round</label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: '30s', val: 30 },
                  { label: '60s', val: 60 },
                  { label: '90s', val: 90 },
                  { label: '∞', val: 0 }
                ].map(opt => (
                  <button
                    key={opt.val}
                    onClick={() => setTimeLimit(opt.val)}
                    className={`py-2 rounded-xl font-bold text-sm transition-all ${
                      timeLimit === opt.val
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Categories Selection */}
          <div className="mt-6 max-w-xl mx-auto text-left">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Categories ({selectedCategories.length} selected)
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {DEFAULT_STOP_CATEGORIES.map(cat => {
                const isSelected = selectedCategories.some(c => c.id === cat.id);
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      if (isSelected) {
                        if (selectedCategories.length > 3) {
                          setSelectedCategories(selectedCategories.filter(c => c.id !== cat.id));
                        }
                      } else {
                        setSelectedCategories([...selectedCategories, cat]);
                      }
                    }}
                    className={`p-2.5 rounded-xl border text-left text-xs font-bold flex items-center gap-2 transition-all ${
                      isSelected
                        ? 'bg-purple-50 border-purple-300 text-purple-900 shadow-sm'
                        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span>{cat.icon}</span>
                    <span className="truncate">{cat.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-8">
            <button
              onClick={handleStartGame}
              className="px-10 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-extrabold text-lg rounded-2xl shadow-xl shadow-purple-500/25 transition-all transform hover:scale-105"
            >
              Start Game!
            </button>
          </div>
        </div>
      )}

      {/* PLAYING PHASE */}
      {phase === 'playing' && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100 flex flex-col items-center">
            <StopLetterWheel
              letter={currentLetter}
              isSpinning={isSpinning}
              onSpinComplete={() => setIsSpinning(false)}
            />

            {timeLimit > 0 && (
              <div className="w-full max-w-md mt-4">
                <div className="flex items-center justify-between text-sm font-bold mb-1">
                  <span className="flex items-center gap-1 text-gray-600">
                    <Timer className="w-4 h-4" />
                    Time Remaining
                  </span>
                  <span
                    className={`font-mono text-base ${
                      timeLeft <= 10 ? 'text-red-600 animate-pulse' : 'text-gray-900'
                    }`}
                  >
                    {timeLeft}s
                  </span>
                </div>
                <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-1000 ${
                      timeLeft <= 10 ? 'bg-red-500' : 'bg-gradient-to-r from-purple-500 to-indigo-500'
                    }`}
                    style={{ width: `${(timeLeft / timeLimit) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Spinning Reel Notice or Category Input Fields */}
          {isSpinning && (
            <div className="p-4 bg-gradient-to-r from-purple-100 via-pink-100 to-indigo-100 text-purple-900 font-extrabold text-sm rounded-2xl text-center border border-purple-200 animate-pulse shadow-sm">
              🎲 Spinning letter reel... Game begins in 3 seconds!
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selectedCategories.map((cat, idx) => (
              <StopCategoryInput
                key={cat.id}
                category={cat}
                letter={currentLetter}
                value={answers[cat.id] || ''}
                onChange={val => handleAnswerChange(cat.id, val)}
                autoFocus={idx === 0 && !isSpinning}
                disabled={isSpinning}
              />
            ))}
          </div>

          {/* STOP Button */}
          <div className="text-center pt-4">
            <button
              onClick={handleStop}
              disabled={isSpinning}
              className={`w-full md:w-auto px-16 py-4 bg-gradient-to-r from-red-600 via-rose-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white font-black text-2xl tracking-wider uppercase rounded-2xl shadow-xl shadow-red-500/30 transition-all transform hover:scale-105 active:scale-95 border-2 border-white/20 ${
                isSpinning ? 'opacity-50 cursor-not-allowed transform-none' : ''
              }`}
            >
              🛑 STOP!
            </button>
            <p className="text-xs text-gray-500 mt-2 font-medium">
              Click STOP as soon as you finish to check your answers!
            </p>
          </div>
        </div>
      )}

      {/* VALIDATING PHASE */}
      {phase === 'validating' && (
        <div className="bg-white rounded-3xl p-12 text-center shadow-xl border border-gray-100">
          <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-6" />
          <h3 className="text-2xl font-black text-gray-900">Validating English Words...</h3>
          <p className="text-gray-600 mt-2">
            Consulting the English dictionary API to verify spelling, definitions, and categories.
          </p>
        </div>
      )}

      {/* ROUND REVIEW PHASE */}
      {phase === 'round_review' && (
        <StopWordReviewModal
          categories={selectedCategories}
          playerAnswers={answersRef.current}
          validations={validations}
          categoryScores={categoryScores}
          totalRoundScore={roundScore}
          letter={currentLetter}
          onNextRound={handleNextRoundOrFinish}
          nextButtonText={currentRound >= totalRounds ? 'See Final Results 🏆' : 'Next Round ➡️'}
        />
      )}

      {/* GAME OVER PHASE */}
      {phase === 'game_over' && (
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 text-center max-w-xl mx-auto animate-in zoom-in-95">
          <div className="w-20 h-20 bg-gradient-to-tr from-amber-400 to-yellow-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl shadow-amber-400/30">
            <Trophy className="w-10 h-10" />
          </div>
          <h2 className="text-3xl font-black text-gray-900">Game Complete!</h2>
          <p className="text-gray-600 mt-1">Fantastic practice! Here is your final summary:</p>

          <div className="my-6 p-6 bg-purple-50 rounded-2xl border border-purple-200">
            <span className="text-sm font-bold text-purple-700 uppercase tracking-wide">Final Score</span>
            <div className="text-5xl font-black text-purple-900 mt-1">{totalScore} Points</div>
          </div>

          <div className="space-y-2 mb-8 text-left">
            <h4 className="text-xs font-bold uppercase text-gray-500 tracking-wider">Round Breakdown</h4>
            {roundHistory.map(r => (
              <div
                key={r.round}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-xl text-sm font-semibold"
              >
                <span>Round {r.round} (Letter {r.letter})</span>
                <span className="text-purple-700">+{r.score} pts</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleStartGame}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Play Again
            </button>
            <button
              onClick={handleChangeSettings}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-all"
            >
              Change Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StopGameSolo;
